import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Route, Send, Clock, CheckCircle2, AlertTriangle, Ban, ChevronDown, Loader2, MessageCircle, Mail,
} from "lucide-react";

const BADGE = {
  pronto: { cls: "bg-emerald-100 text-emerald-700", label: "Pronto", Icon: CheckCircle2 },
  da_configurare: { cls: "bg-amber-100 text-amber-800", label: "Da configurare", Icon: AlertTriangle },
  inviato: { cls: "bg-slate-200 text-slate-600", label: "Inviato", Icon: CheckCircle2 },
  non_applicabile: { cls: "bg-slate-100 text-slate-400", label: "Non applicabile", Icon: Ban },
  disattivato: { cls: "bg-slate-100 text-slate-400", label: "Disattivato", Icon: Ban },
};
const CH = { whatsapp: MessageCircle, email: Mail };

const fmtDT = (s) => s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

export default function CustomerJourney({ bookingId, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [sending, setSending] = useState("");
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/comm/journey/${bookingId}?base_url=${encodeURIComponent(baseUrl)}`)
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [bookingId, baseUrl]);
  useEffect(() => { load(); }, [load]);

  const send = async (code, channel) => {
    setSending(code);
    try {
      await api.post(`/comm/journey/${bookingId}/${code}/send`, { channel, base_url: baseUrl });
      toast.success(`${code} inviato via ${channel}`);
      load(); onChange?.();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det && typeof det === "object" && det.unresolved) {
        toast.error(`Invio bloccato: ${det.unresolved.map((u) => u.placeholder).join(", ")} non risolti`);
      } else {
        toast.error(det || "Errore invio");
      }
    } finally { setSending(""); }
  };

  const setChannel = (code, ch) => setData((d) => ({ ...d, steps: d.steps.map((s) => s.code === code ? { ...s, channel: ch } : s) }));

  if (loading) return <div className="flex items-center gap-2 py-6 text-sm text-slate-400" data-testid="journey-loading"><Loader2 size={16} className="animate-spin" /> Caricamento Customer Journey…</div>;
  if (!data) return null;

  return (
    <div data-testid="customer-journey">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Route size={16} className="text-indigo-600" /> Customer Journey</div>
        {data.next_suggested && (
          <Badge className="bg-indigo-100 text-indigo-700" data-testid="journey-next-suggested">
            Prossimo suggerito: {data.next_suggested}
          </Badge>
        )}
      </div>

      <div className="space-y-2">
        {data.steps.map((s) => {
          const b = BADGE[s.badge] || BADGE.da_configurare;
          const isOpen = open[s.code];
          const isNext = s.code === data.next_suggested;
          const canSend = s.applicable && s.enabled && s.status !== "sent" && s.can_auto_send;
          const ChIcon = CH[s.channel] || MessageCircle;
          return (
            <div key={s.code} data-testid={`journey-step-${s.code}`}
              className={`rounded-xl border p-3 transition-colors ${isNext ? "border-indigo-300 bg-indigo-50/40" : "border-slate-200"} ${!s.applicable ? "opacity-60" : ""}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <button className="flex min-w-0 items-center gap-2 text-left" data-testid={`journey-toggle-${s.code}`}
                  onClick={() => setOpen((o) => ({ ...o, [s.code]: !o[s.code] }))}>
                  <ChevronDown size={15} className={`shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  <span className="shrink-0 rounded-md bg-slate-800 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-white">{s.code}</span>
                  <span className="truncate text-sm font-medium text-slate-800">{s.title}</span>
                </button>
                <div className="flex items-center gap-2">
                  <span className="hidden items-center gap-1 text-xs text-slate-400 sm:flex" data-testid={`journey-schedule-${s.code}`}><Clock size={12} /> {fmtDT(s.scheduled_at)}</span>
                  <Badge className={b.cls} data-testid={`journey-badge-${s.code}`}><b.Icon size={12} className="mr-1" /> {b.label}</Badge>
                </div>
              </div>

              {isOpen && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  {!s.applicable ? (
                    <p className="text-xs text-slate-500" data-testid={`journey-reason-${s.code}`}>{s.condition_reason}</p>
                  ) : (
                    <>
                      <div className="mb-1 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">Anteprima messaggio</div>
                      <div className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm text-slate-700" data-testid={`journey-preview-${s.code}`}>{s.preview}</div>
                      {!s.can_auto_send && s.status !== "sent" && (
                        <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid={`journey-blocked-${s.code}`}>
                          <AlertTriangle size={13} className="mr-1 inline" /> Invio bloccato · placeholder non risolti: {(s.unresolved || []).map((u) => u.placeholder).join(", ")}
                        </div>
                      )}
                      {s.status === "sent" && (
                        <p className="mt-2 text-xs text-slate-500" data-testid={`journey-sent-${s.code}`}>Inviato {s.sent_at ? `il ${fmtDT(s.sent_at)}` : ""}{s.sent_by ? ` da ${s.sent_by}` : ""} via {s.channel}</p>
                      )}
                      {s.status !== "sent" && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <div className="w-40">
                            <Select value={s.channel} onValueChange={(v) => setChannel(s.code, v)}>
                              <SelectTrigger className="h-9" data-testid={`journey-channel-${s.code}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                                <SelectItem value="email">Email</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button size="sm" className="gap-1.5" disabled={!canSend || sending === s.code}
                            data-testid={`journey-send-${s.code}`} onClick={() => send(s.code, s.channel)}>
                            {sending === s.code ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                            Invia manualmente
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
